/** The library categories the walkthrough visits, in order. */
export const INTERESTS = [
  { key: "sports", label: "Sports" },
  { key: "tv", label: "TV shows" },
  { key: "movies", label: "Movies" },
];

export const WALKTHROUGH = INTERESTS.map((it) => it.key);
export const INTEREST_LABELS = Object.fromEntries(INTERESTS.map((it) => [it.key, it.label]));

/** Adds that count as real product value; drives the header progress meter. */
export const GOAL_CALENDARS = 5;

/** Section for loose calendars that carry no walkthrough interest. */
export const OTHER_SECTION_LABEL = "Already in your library";

/** sessionStorage key holding the final-step feed ids across the Google Connect round-trip. */
export const HANDOFF_FEEDS_KEY = "trialActivationHandoffFeeds";

const HANDOFF_FEEDS_PARAM = "activation_feeds";
const OAUTH_RETURN_FLAGS = { connected: "connected", connectError: "error" };
const validHandoffIds = (ids) => new Set(ids.filter((id) => typeof id === "string" && /^[a-zA-Z0-9_-]{1,64}$/.test(id)));

/** Feed selection carried by a cross-browser return URL. */
export function handoffFeedIdsFrom(search) {
  const ids = new URLSearchParams(search).get(HANDOFF_FEEDS_PARAM);
  return validHandoffIds(ids ? ids.split(",") : []);
}

/** Page URL retaining the final feed selection when opened in another browser. */
export function buildHandoffPageUrl(ids, pageUrl) {
  const url = new URL(pageUrl);
  for (const key of Object.keys(OAUTH_RETURN_FLAGS)) url.searchParams.delete(key);
  url.searchParams.set(HANDOFF_FEEDS_PARAM, [...validHandoffIds(ids)].join(","));
  return url.toString();
}

/**
 * Reads how the page was reached from the Google Connect OAuth callback.
 *
 * @param {string} search - `window.location.search`.
 * @returns {"connected"|"error"|null} The OAuth outcome, or null for a normal visit.
 */
export function oauthReturnFrom(search) {
  const params = new URLSearchParams(search);
  for (const [key, outcome] of Object.entries(OAUTH_RETURN_FLAGS)) {
    if (params.get(key) === "1") return outcome;
  }
  return null;
}

/**
 * Saves the final-step feed ids before leaving for Google's consent screen.
 *
 * @param {string[]} ids - Calendar and group ids offered in the final step.
 * @param {Storage} [storage] - Defaults to sessionStorage.
 */
export function stashHandoffFeedIds(ids, storage) {
  try {
    (storage ?? window.sessionStorage).setItem(HANDOFF_FEEDS_KEY, JSON.stringify(ids.filter(Boolean)));
  } catch {
    /* storage unavailable: the return lands without the feed list */
  }
}

/**
 * Reads the feed ids stashed by {@link stashHandoffFeedIds}.
 *
 * @param {Storage} [storage] - Defaults to sessionStorage.
 * @returns {Set<string>} The stashed ids; empty when missing or unreadable.
 */
export function readHandoffFeedIds(storage) {
  try {
    const ids = JSON.parse((storage ?? window.sessionStorage).getItem(HANDOFF_FEEDS_KEY) || "[]");
    return new Set(Array.isArray(ids) ? ids : []);
  } catch {
    return new Set();
  }
}

/**
 * Clears the stashed feed ids once the return has consumed them.
 *
 * @param {Storage} [storage] - Defaults to sessionStorage.
 */
export function clearHandoffFeedIds(storage) {
  try {
    (storage ?? window.sessionStorage).removeItem(HANDOFF_FEEDS_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Applies a SubscribeButton toggle to the calendarId→interest map.
 *
 * @param {Map<string, string>} map - Current flow adds (calendarId → interest).
 * @param {{ added: boolean, calendarId?: string, interest: string }} toggle
 * @returns {Map<string, string>|null} New map, or null when nothing changes
 *   (duplicate add, or removal of a calendar the flow never added).
 */
export function applyCalendarToggle(map, { added, calendarId, interest }) {
  if (!calendarId) return null;
  if (added) {
    if (map.has(calendarId)) return null;
    const next = new Map(map);
    next.set(calendarId, interest);
    return next;
  }
  if (!map.has(calendarId)) return null;
  const next = new Map(map);
  next.delete(calendarId);
  return next;
}

/**
 * Counts flow adds per interest from the calendarId→interest map.
 *
 * @param {Map<string, string>} map
 * @returns {Object<string, number>}
 */
export function deriveByInterest(map) {
  const byInterest = {};
  for (const interest of map.values()) {
    byInterest[interest] = (byInterest[interest] || 0) + 1;
  }
  return byInterest;
}

/**
 * Drops finished sections from the per-section grouping map: a section stays
 * active while any of its calendar ids are still loose, and clears once they
 * have all moved into a group. Returns the same object when nothing changed.
 *
 * @param {Object<string, string[]>} active - sectionKey → calendar ids mid-move.
 * @param {Set<string>|Array<string>} liveIds - calendar ids still in the loose list.
 * @returns {Object<string, string[]>}
 */
export function reconcileActiveGroups(active, liveIds) {
  if (!active) return {};
  const live = liveIds instanceof Set ? liveIds : new Set(liveIds || []);
  let changed = false;
  const next = {};
  for (const [key, ids] of Object.entries(active || {})) {
    if ((ids || []).some((id) => live.has(id))) next[key] = ids;
    else changed = true;
  }
  return changed ? next : active;
}

/**
 * Entry intents carried on the onboarding URL: a trial-start ask and a seed
 * calendar to add first (the one the user was adding when they upgraded).
 *
 * @param {string} search - `window.location.search`.
 * @returns {{ trial: boolean, seedId: string|null, seedHandle: string|null }}
 */
export function entryParamsFrom(search) {
  const params = new URLSearchParams(search);
  return {
    trial: params.get("trial") === "1",
    seedId: params.get("seed") || null,
    seedHandle: params.get("seedHandle") || null,
    app: params.get("app") || null,
    device: params.get("device") || null,
  };
}

/**
 * The inverse of {@link entryParamsFrom}: builds the onboarding entry URL so
 * every producer shares one param vocabulary with the parser.
 *
 * @param {string} base - Path the query string is appended to.
 * @param {{ trial?: boolean, seedId?: string|null, seedHandle?: string|null, app?: string|null, device?: string|null }} [entry]
 * @returns {string}
 */
/**
 * Trial-onboarding entry URL seeded with a checkout's calendar (both id and
 * handle required for a usable seed); the shared fallback for surfaces that
 * aren't handed a richer destination.
 *
 * @param {string} base - Path the query string is appended to.
 * @param {{ _id?: string, handle?: string }|null} [calendar]
 * @returns {string}
 */
export function buildSeededTrialUrl(base, calendar) {
  const seeded = Boolean(calendar?._id && calendar?.handle);
  return buildTrialOnboardingUrl(base, {
    trial: true,
    seedId: seeded ? calendar._id : null,
    seedHandle: seeded ? calendar.handle : null,
  });
}

export function buildTrialOnboardingUrl(base, { trial = false, seedId = null, seedHandle = null, app = null, device = null } = {}) {
  const params = new URLSearchParams();
  if (trial) params.set("trial", "1");
  if (seedId) params.set("seed", seedId);
  if (seedHandle) params.set("seedHandle", seedHandle);
  if (app) params.set("app", app);
  if (device) params.set("device", device);
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Releases a section's grouping lock, but only when the lock belongs to the
 * settling op: a newer op may have re-armed the same section with other ids.
 *
 * @param {Object<string, string[]>} active - sectionKey → calendar ids mid-move.
 * @param {string} key - The settling op's section.
 * @param {string[]} ids - The settling op's calendar ids.
 * @returns {Object<string, string[]>} Same object when nothing released.
 */
export function releaseSectionLock(active, key, ids) {
  const armed = active?.[key];
  if (!armed || armed.length !== ids.length || !armed.every((id) => ids.includes(id))) return active;
  const { [key]: _dropped, ...rest } = active;
  return rest;
}

/**
 * Splits loose calendars into the walkthrough's genre sections (plus a trailing
 * catch-all), in walkthrough order, dropping empty sections.
 *
 * @param {Array<object>} calendars - Calendar docs (`_id`) or tiles (`calendarId`).
 * @param {Object<string, string>} interestById - calendarId → interest it was added under.
 * @returns {Array<{ key: string, label: string, items: Array<object> }>}
 */
export function sectionLooseCalendars(calendars, interestById = {}) {
  const defs = [...INTERESTS, { key: "other", label: OTHER_SECTION_LABEL }];
  const sections = defs.map((d) => ({ key: d.key, label: d.label, items: [] }));
  const byKey = Object.fromEntries(sections.map((s) => [s.key, s]));
  for (const calendar of calendars || []) {
    const id = calendar?._id ?? calendar?.calendarId;
    const key = (id && interestById[id]) || "other";
    (byKey[key] || byKey.other).items.push(calendar);
  }
  return sections.filter((s) => s.items.length > 0);
}

/**
 * The calendars and groups this session added, to hand off at the end of the flow.
 *
 * @param {object} params
 * @param {{ addedCalendars?: object[], createdGroups?: object[] }} [params.userCalendars]
 * @param {Set<string>} params.addedIds - Calendar ids added this session (live ids included).
 * @param {Set<string>} params.movingIds - Calendar ids whose move into a group is still in flight.
 * @param {Set<string>} params.baselineGroupIds - Group ids that existed before the session.
 * @param {Set<string>} params.sessionGroupIds - Group ids created or extended this session.
 * @param {Set<string>} params.returnedFeedIds - Feed ids stashed across the OAuth round-trip.
 * @returns {object[]} Calendars, then groups.
 */
export function selectSessionFeeds({
  userCalendars,
  addedIds,
  movingIds,
  baselineGroupIds,
  sessionGroupIds,
  returnedFeedIds,
}) {
  return [
    ...(userCalendars?.addedCalendars || []).filter(
      (c) => !movingIds.has(c._id) && (addedIds.has(c._id) || returnedFeedIds.has(c._id)),
    ),
    ...(userCalendars?.createdGroups || []).filter(
      (g) => !baselineGroupIds.has(g._id) || sessionGroupIds.has(g._id) || returnedFeedIds.has(g._id),
    ),
  ];
}

/**
 * Unlabeled setup progress: a head start for arriving, most of the bar from
 * calendars (pre-existing ones count toward the goal), the last slice from a group.
 *
 * @param {{ addedCount: number, preexisting: number, groupCount: number }} state
 * @returns {number} 0-100
 */
export function computeSetupProgress({ addedCount, preexisting = 0, groupCount = 0 }) {
  return Math.min(
    100,
    10 + Math.min(addedCount + preexisting, GOAL_CALENDARS) * 15 + (groupCount > 0 ? 15 : 0),
  );
}

/**
 * The interest after `key` in walkthrough order, or "done" past the last.
 *
 * @param {string} key
 * @returns {string}
 */
export function nextInterestAfter(key) {
  return WALKTHROUGH[WALKTHROUGH.indexOf(key) + 1] || "done";
}

/**
 * Sticky-button label: "Next" once the step produced an add, "Skip to" while
 * it hasn't; the last interest leads into the grouping step.
 *
 * @param {{ key: string, byInterest: Object<string, number> }} state
 * @returns {string}
 */
export function nextButtonLabel({ key, byInterest }) {
  const count = byInterest[key] || 0;
  const next = nextInterestAfter(key);
  if (next !== "done") {
    return count > 0 ? `Next: ${INTEREST_LABELS[next]}` : `Skip to ${INTEREST_LABELS[next]}`;
  }
  return count > 0 ? "Next: group your calendars" : "Skip to grouping";
}
